// SPDX-License-Identifier: AGPL-3.0-or-later
import i18n from '@app/app/I18n';
import {ForkEndpoints} from '@app/features/app/constants/ForkEndpoints';
import EmojiPins from '@app/features/emoji/state/EmojiPins';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {http} from '@app/features/platform/transport/RestTransport';
import * as Toast from '@app/features/ui/commands/ToastCommands';
import type {
	ActivePersonaScope,
	PersonaChannelState,
	PersonaChannelStatesResponse,
	PersonaResponse,
	PersonaSettingsResponse,
	PersonaTag,
	PersonaVisibility,
	SignatureEmoji,
} from '@fluxer/schema/src/domains/persona/PersonaApiSchemas';
import {
	isUnfinishedPersonaTag,
	type MatchPersonaOptions,
	type MatchResult,
	matchPersona,
	type PersonaLike,
	previewPersona,
} from '@fluxer/schema/src/domains/persona/PersonaMatcher';
import type {
	MessageSubprofileResponse,
	PersonaLookupItem,
	PersonaLookupResponse,
} from '@fluxer/schema/src/domains/persona/PersonaSchemas';
import {msg} from '@lingui/core/macro';
import {makeAutoObservable, observable, runInAction} from 'mobx';
import {PersonaLookupBatcher, type PersonaLookupPair} from './PersonaLookupBatcher';

const DISPLAY_TAG_UPDATE_FAILED_DESCRIPTOR = msg`Failed to update display tag`;
const PERSONA_MODE_UPDATE_FAILED_DESCRIPTOR = msg`Failed to update persona mode`;
const SET_ACTIVE_PERSONA_FAILED_DESCRIPTOR = msg`Failed to set active persona`;
const CLEAR_ACTIVE_PERSONA_FAILED_DESCRIPTOR = msg`Failed to clear active persona`;
const PERSONA_SCOPE_UPDATE_FAILED_DESCRIPTOR = msg`Failed to update where your persona is remembered`;
const CHANNEL_PERSONA_UPDATE_FAILED_DESCRIPTOR = msg`Failed to set the persona for this channel`;
const ACTIVE_PERSONA_CLEARED_DESCRIPTOR = msg`Active persona cleared`;
const ACTIVE_PERSONA_CLEARED_ROOT_DESCRIPTOR = msg`Active persona cleared (sending as root account)`;

export type ActivePersonaMode = 'manual' | 'last';

/** A persona of this account, as the server sends it. */
export type ClientPersona = PersonaResponse;

export type Persona = ClientPersona;

/**
 * The copy of a persona that is drawn beside a message, from the persona itself or from such a
 * copy. A persona calls its avatar `avatar_hash`; the copy calls it `avatar`.
 */
export function normalizeSubprofile(
	input: PersonaResponse | MessageSubprofileResponse,
	overrides?: {display_tag_text?: string | null; display_tag_icon?: string | null},
): MessageSubprofileResponse {
	const source = input as any;
	return {
		id: source.id,
		name: source.name,
		avatar: source.avatar_hash !== undefined ? source.avatar_hash : source.avatar,
		avatar_color: source.avatar_color,
		display_tag_text: overrides?.display_tag_text !== undefined ? overrides.display_tag_text : source.display_tag_text,
		display_tag_icon: overrides?.display_tag_icon !== undefined ? overrides.display_tag_icon : source.display_tag_icon,
		color: source.color,
		visibility: source.visibility,
	};
}

/** A persona as the store holds it: the server's fields, with what the server left out filled in. */
export function normalizePersona(
	raw: PersonaResponse | (Partial<PersonaResponse> & {id: string; name: string}) | {persona: PersonaResponse},
): ClientPersona {
	const source: any = (raw as any)?.persona ?? raw;
	const tags: Array<PersonaTag> = (source.persona_tags ?? []).map((t: any) => ({
		prefix: t.prefix ?? undefined,
		suffix: t.suffix ?? undefined,
	}));
	const signatureEmojis: Array<SignatureEmoji> = (source.signature_emojis ?? []).map((e: any) => ({
		id: e.id ?? null,
		name: e.name ?? '',
		animated: e.animated ?? null,
	}));
	const lastUsedRaw = source.last_used_at_ms;

	return {
		id: source.id ?? '',
		name: source.name ?? '',
		avatar_hash: source.avatar_hash ?? null,
		banner_hash: source.banner_hash ?? null,
		pronouns: source.pronouns ?? null,
		color: source.color ?? null,
		avatar_color: source.avatar_color ?? null,
		bio: source.bio ?? null,
		auto_tag_disabled: Boolean(source.auto_tag_disabled),
		persona_tags: tags,
		signature_emojis: signatureEmojis,
		use_count: source.use_count ?? 0,
		last_used_at_ms: lastUsedRaw != null ? String(lastUsedRaw) : null,
		visibility: (source.visibility ?? 'unlisted') as PersonaVisibility,
		external_uuid: source.external_uuid ?? null,
		created_at: source.created_at ?? new Date().toISOString(),
		updated_at: source.updated_at ?? new Date().toISOString(),
	};
}

const personaNameCollator = new Intl.Collator(undefined, {numeric: true});
const LEADING_DECORATION = /^[^\p{Alphabetic}\p{N}]+/u;

/** The name as it is filed in sorted lists: without leading symbols or emoji, so "✨Alice" sits with the A's. */
export function getPersonaSortName(name: string): string {
	const trimmed = name.trim();
	return trimmed.replace(LEADING_DECORATION, '') || trimmed;
}

export function comparePersonasByName(a: ClientPersona, b: ClientPersona): number {
	return (
		personaNameCollator.compare(getPersonaSortName(a.name), getPersonaSortName(b.name)) || a.id.localeCompare(b.id)
	);
}

/** What a message shows of a looked-up persona, without the owner the lookup adds. */
function lookupItemToSubprofile(item: PersonaLookupItem): MessageSubprofileResponse {
	const {user_id: _ownerId, ...subprofile} = item;
	return subprofile;
}

// Fork: every persona lookup goes through one batcher, so lookups made close together share a request.
const personaLookups = new PersonaLookupBatcher(async (pairs: Array<PersonaLookupPair>) => {
	const res = await http.post<PersonaLookupResponse>(ForkEndpoints.PERSONA_LOOKUP, {
		body: {personas: pairs.map(({userId, personaId}) => ({user_id: userId, persona_id: personaId}))},
	});
	return res.body ?? [];
});

export class PersonaStoreClass {
	private _personas: Array<ClientPersona> = [];
	private _displayTagText: string = '';
	private _displayTagIcon: string | null = null;
	private _activePersonaMode: ActivePersonaMode = 'manual';
	private _activePersonaId: string | null = null;
	private _isPersonaLatched: boolean = false;
	private _activePersonaScope: ActivePersonaScope = 'global';
	/** With the per-channel scope: the persona selected in each channel. No entry means the account. */
	private readonly _channelPersonaIds = observable.map<string, string>();
	private _knownPersonas = new Map<string, MessageSubprofileResponse>();
	/** The account each known persona belongs to, where it is known, so an account's edits can be fetched. */
	private _knownPersonaOwners = new Map<string, string>();

	constructor() {
		makeAutoObservable(this);
	}

	reset(): void {
		runInAction(() => {
			this._personas = [];
			this._displayTagText = '';
			this._displayTagIcon = null;
			this._activePersonaMode = 'manual';
			this._activePersonaId = null;
			this._isPersonaLatched = false;
			this._activePersonaScope = 'global';
			this._channelPersonaIds.clear();
			this._knownPersonas.clear();
			this._knownPersonaOwners.clear();
		});
		personaLookups.reset();
	}

	get personas(): ReadonlyArray<ClientPersona> {
		return this._personas;
	}

	getPersona(id: string): ClientPersona | null {
		return this._personas.find((p) => p.id === id) ?? null;
	}

	getKnownPersona(personaId: string): MessageSubprofileResponse | null {
		const own = this.getPersona(personaId);
		if (own) {
			return normalizeSubprofile(own, {
				display_tag_text: this.displayTagText || null,
				display_tag_icon: this.displayTagIcon || null,
			});
		}
		return this._knownPersonas.get(personaId) ?? null;
	}

	/** Remembers a persona seen beside a message, reaction, signal or mention, with its owner when known. */
	recordKnownPersona(persona: MessageSubprofileResponse, ownerId?: string | null): void {
		runInAction(() => {
			this._knownPersonas.set(persona.id, persona);
			if (ownerId) this._knownPersonaOwners.set(persona.id, ownerId);
		});
	}

	/** The known personas of another account, by ID. */
	getKnownPersonaIdsOf(userId: string): Array<string> {
		const ids: Array<string> = [];
		for (const [personaId, ownerId] of this._knownPersonaOwners) {
			if (ownerId === userId && this._knownPersonas.has(personaId)) ids.push(personaId);
		}
		return ids;
	}

	/**
	 * What a message from a persona shows, from memory or looked up by ID (lookups made close together
	 * share one request). Null when it cannot be found or the lookup failed recently.
	 */
	async fetchPersona(userId: string, personaId: string): Promise<MessageSubprofileResponse | null> {
		const existing = this.getKnownPersona(personaId);
		if (existing) return existing;
		const item = await personaLookups.lookup({userId, personaId});
		if (!item) return null;
		const subprofile = lookupItemToSubprofile(item);
		this.recordKnownPersona(subprofile, item.user_id);
		return subprofile;
	}

	/**
	 * Fetches the given personas again, known or not (after their owner edited one), and remembers what
	 * comes back. Returns the personas found; one request for all of them.
	 */
	async refreshPersonas(pairs: ReadonlyArray<PersonaLookupPair>): Promise<Array<MessageSubprofileResponse>> {
		const items = await Promise.all(pairs.map((pair) => personaLookups.lookup(pair, {force: true})));
		const found: Array<MessageSubprofileResponse> = [];
		for (const item of items) {
			if (!item) continue;
			const subprofile = lookupItemToSubprofile(item);
			this.recordKnownPersona(subprofile, item.user_id);
			found.push(subprofile);
		}
		return found;
	}

	setPersonas(
		personas: Array<PersonaResponse | ClientPersona> | {personas: Array<PersonaResponse | ClientPersona>},
	): void {
		runInAction(() => {
			const wrapped = Array.isArray(personas) ? personas : (personas as any)?.personas;
			const list = Array.isArray(wrapped) ? wrapped : [];
			this._personas = list
				.filter((p: any) => Boolean((p as any)?.id || (p as any)?.persona?.id))
				.map(normalizePersona);
		});
		if (this._activePersonaId && !this._personas.some((p) => p.id === this._activePersonaId)) {
			void this.unlatch();
		}
	}

	upsertPersona(persona: PersonaResponse | ClientPersona | {persona: PersonaResponse}): void {
		runInAction(() => {
			const normalized = normalizePersona(persona);
			if (!normalized.id) {
				return;
			}
			const idx = this._personas.findIndex((p) => p.id === normalized.id);
			if (idx >= 0) {
				this._personas[idx] = normalized;
			} else {
				this._personas.push(normalized);
			}
			this._personas = [...this._personas];
			if (this._knownPersonas.has(normalized.id)) {
				this._knownPersonas.set(
					normalized.id,
					normalizeSubprofile(normalized, {
						display_tag_text: this.displayTagText || null,
						display_tag_icon: this.displayTagIcon || null,
					}),
				);
			}
		});
	}

	upsertPersonas(
		personas: Array<PersonaResponse | ClientPersona> | {personas: Array<PersonaResponse | ClientPersona>},
	): void {
		runInAction(() => {
			const wrapped = Array.isArray(personas) ? personas : (personas as any)?.personas;
			const list = Array.isArray(wrapped) ? wrapped : [];
			for (const p of list) {
				const normalized = normalizePersona(p);
				if (!normalized.id) {
					continue;
				}
				const idx = this._personas.findIndex((item) => item.id === normalized.id);
				if (idx >= 0) {
					this._personas[idx] = normalized;
				} else {
					this._personas.push(normalized);
				}
			}
			this._personas = [...this._personas];
		});
	}

	removePersona(id: string): void {
		if (!id) {
			return;
		}
		runInAction(() => {
			this._personas = this._personas.filter((p) => p.id !== id);
			this._knownPersonas.delete(id);
		});
		EmojiPins.forgetPersona(id);
		if (this.activePersonaId === id) {
			void this.unlatch();
		}
	}

	get activePersonaId(): string | null {
		return this._activePersonaId && this._activePersonaId.length > 0 ? this._activePersonaId : null;
	}

	get activePersonaMode(): ActivePersonaMode {
		return this._activePersonaMode;
	}

	get isPersonaLatched(): boolean {
		return this._isPersonaLatched;
	}

	get activePersona(): ClientPersona | null {
		const id = this.activePersonaId;
		if (!id) return null;
		return this._personas.find((p) => p.id === id) ?? null;
	}

	/** `global`: one selected persona for every channel. `channel`: each channel remembers its own. */
	get activePersonaScope(): ActivePersonaScope {
		return this._activePersonaScope;
	}

	/**
	 * The persona selected for a channel: what an untagged message there is sent as, and what its
	 * composer shows. Everything that acts in a channel asks this, never `activePersona`, which is
	 * only the selection for every channel and says nothing about one channel when the selection is
	 * kept per channel. Without a channel (null) there is nothing per channel to look up, and the
	 * answer is the selection for every channel, or nobody when selections are per channel.
	 */
	latchedPersonaFor(channelId: string | null): ClientPersona | null {
		if (this._activePersonaScope === 'channel') {
			const id = channelId ? this._channelPersonaIds.get(channelId) : undefined;
			return id ? (this._personas.find((p) => p.id === id) ?? null) : null;
		}
		return this._isPersonaLatched ? this.activePersona : null;
	}

	get displayTagText(): string {
		return this._displayTagText;
	}

	get displayTagIcon(): string | null {
		return this._displayTagIcon && this._displayTagIcon.length > 0 ? this._displayTagIcon : null;
	}

	updateSettings(settings: Partial<PersonaSettingsResponse>): void {
		runInAction(() => {
			if (settings.active_persona_mode !== undefined) {
				this._activePersonaMode = settings.active_persona_mode;
			}
			if (settings.active_persona_scope !== undefined) {
				this._activePersonaScope = settings.active_persona_scope;
			}
			if (settings.active_persona_id !== undefined) {
				this._activePersonaId = settings.active_persona_id;
			}
			if (settings.is_latched !== undefined) {
				this._isPersonaLatched = settings.is_latched;
			}
			if (settings.display_tag_text !== undefined) {
				this._displayTagText = settings.display_tag_text ?? '';
			}
			if (settings.display_tag_icon !== undefined) {
				this._displayTagIcon = settings.display_tag_icon ?? null;
			}
		});
	}

	async setDisplayTag(text: string, icon?: string | null): Promise<void> {
		const prevText = this._displayTagText;
		const prevIcon = this._displayTagIcon;
		const newText = text.trim();
		let newIcon = this._displayTagIcon;
		if (icon !== undefined) {
			newIcon = icon ? icon.trim() : null;
		}
		runInAction(() => {
			this._displayTagText = newText;
			this._displayTagIcon = newIcon;
		});
		try {
			await http.patch(ForkEndpoints.USER_PERSONA_SETTINGS, {
				body: {
					display_tag_text: newText,
					display_tag_icon: newIcon,
				},
			});
		} catch {
			runInAction(() => {
				this._displayTagText = prevText;
				this._displayTagIcon = prevIcon;
			});
			Toast.error(i18n._(DISPLAY_TAG_UPDATE_FAILED_DESCRIPTOR));
		}
	}

	/** Personas by name, for the lists people browse. `personas` keeps the order they were loaded in. */
	get sortedPersonas(): ReadonlyArray<ClientPersona> {
		return [...this._personas].sort(comparePersonasByName);
	}

	get rankedPersonas(): ReadonlyArray<ClientPersona> {
		const now = Date.now();
		return [...this._personas].sort((a, b) => {
			const scoreA = this.calculateFrecency(a, now);
			const scoreB = this.calculateFrecency(b, now);
			return scoreB - scoreA;
		});
	}

	private calculateFrecency(persona: ClientPersona, nowMs: number): number {
		const lastUsed = Number(persona.last_used_at_ms ?? 0);
		const count = persona.use_count ?? 0;
		if (lastUsed === 0) return 0;
		const hoursAgo = Math.max(0, (nowMs - lastUsed) / (1000 * 60 * 60));
		const recencyFactor = 0.5 ** (hoursAgo / 24);
		return (count + 1) * recencyFactor;
	}

	async setActivePersonaMode(mode: ActivePersonaMode): Promise<void> {
		const prevMode = this._activePersonaMode;
		const prevId = this._activePersonaId;
		const prevLatched = this._isPersonaLatched;

		let targetId: string | null = this._activePersonaId;
		let targetLatched: boolean = this._isPersonaLatched;

		if (mode === 'manual') {
			const currentId = this._activePersonaId;
			targetId = currentId && this._personas.some((p) => p.id === currentId) ? currentId : null;
			targetLatched = Boolean(targetId);
		} else if (mode === 'last') {
			const currentId = this._activePersonaId;
			if (currentId && this._personas.some((p) => p.id === currentId)) {
				targetLatched = true;
			} else {
				targetId = null;
				targetLatched = false;
			}
		}

		runInAction(() => {
			this._activePersonaMode = mode;
			this._activePersonaId = targetId;
			this._isPersonaLatched = targetLatched;
		});

		try {
			await http.patch(ForkEndpoints.USER_PERSONA_SETTINGS, {
				body: {
					active_persona_mode: mode,
					active_persona_id: targetId,
					is_latched: targetLatched,
				},
			});
		} catch {
			runInAction(() => {
				this._activePersonaMode = prevMode;
				this._activePersonaId = prevId;
				this._isPersonaLatched = prevLatched;
			});
			Toast.error(i18n._(PERSONA_MODE_UPDATE_FAILED_DESCRIPTOR));
		}
	}

	/**
	 * Selects a persona. With the per-channel scope and a channel, the selection is that channel's
	 * alone and the selection for every channel is left as it is.
	 */
	async setActivePersona(
		id: string | null,
		latch = true,
		mode?: ActivePersonaMode,
		channelId: string | null = null,
	): Promise<void> {
		if (this._activePersonaScope === 'channel' && channelId) {
			await this.selectInChannel(channelId, id && latch ? id : null);
			return;
		}
		const prevId = this._activePersonaId;
		const prevLatched = this._isPersonaLatched;
		const prevMode = this._activePersonaMode;

		const newMode = mode ?? this._activePersonaMode;
		const newLatched = Boolean(id && latch);

		runInAction(() => {
			this._activePersonaId = id;
			this._isPersonaLatched = newLatched;
			this._activePersonaMode = newMode;
		});

		try {
			await http.patch(ForkEndpoints.USER_PERSONA_SETTINGS, {
				body: {
					active_persona_id: id,
					is_latched: newLatched,
					active_persona_mode: newMode,
				},
			});
		} catch {
			runInAction(() => {
				this._activePersonaId = prevId;
				this._isPersonaLatched = prevLatched;
				this._activePersonaMode = prevMode;
			});
			Toast.error(i18n._(SET_ACTIVE_PERSONA_FAILED_DESCRIPTOR));
		}
	}

	async unlatch(_preserveMode?: boolean, channelId: string | null = null): Promise<void> {
		if (this._activePersonaScope === 'channel' && channelId) {
			await this.selectInChannel(channelId, null);
			return;
		}
		const prevLatched = this._isPersonaLatched;
		const prevId = this._activePersonaId;
		const prevMode = this._activePersonaMode;

		const newMode = this._activePersonaMode;

		runInAction(() => {
			this._isPersonaLatched = false;
			this._activePersonaId = null;
			this._activePersonaMode = newMode;
		});

		try {
			await http.patch(ForkEndpoints.USER_PERSONA_SETTINGS, {
				body: {
					is_latched: false,
					active_persona_id: null,
					active_persona_mode: newMode,
				},
			});
		} catch {
			runInAction(() => {
				this._isPersonaLatched = prevLatched;
				this._activePersonaId = prevId;
				this._activePersonaMode = prevMode;
			});
			Toast.error(i18n._(CLEAR_ACTIVE_PERSONA_FAILED_DESCRIPTOR));
		}
	}

	async setActivePersonaScope(scope: ActivePersonaScope): Promise<void> {
		const previous = this._activePersonaScope;
		if (previous === scope) return;
		runInAction(() => {
			this._activePersonaScope = scope;
		});
		try {
			await http.patch(ForkEndpoints.USER_PERSONA_SETTINGS, {body: {active_persona_scope: scope}});
		} catch {
			runInAction(() => {
				this._activePersonaScope = previous;
			});
			Toast.error(i18n._(PERSONA_SCOPE_UPDATE_FAILED_DESCRIPTOR));
		}
	}

	setChannelStates(states: ReadonlyArray<PersonaChannelState>): void {
		runInAction(() => {
			this._channelPersonaIds.clear();
			for (const state of states) {
				if (state.persona_id) this._channelPersonaIds.set(state.channel_id, state.persona_id);
			}
		});
	}

	/** Another session of this account selected a persona in a channel, or the server cleared one. */
	handleChannelStateUpdate(state: PersonaChannelState): void {
		runInAction(() => {
			if (state.persona_id) {
				this._channelPersonaIds.set(state.channel_id, state.persona_id);
			} else {
				this._channelPersonaIds.delete(state.channel_id);
			}
		});
	}

	/**
	 * A thread or post that was just created starts as whoever opened it, when personas are kept per
	 * channel: they have just spoken there. Opened as the account, it starts as the account like any
	 * other channel.
	 */
	startChannelAs(channelId: string, personaId: string | null | undefined): void {
		if (this._activePersonaScope === 'channel' && personaId) {
			void this.selectInChannel(channelId, personaId);
		}
	}

	async fetchChannelStates(): Promise<void> {
		try {
			const res = await http.get<PersonaChannelStatesResponse>(ForkEndpoints.USER_PERSONA_CHANNEL_STATES);
			if (res.ok && Array.isArray(res.body?.states)) this.setChannelStates(res.body.states);
		} catch {
			// The selections stay as they are; the next load or event corrects them.
		}
	}

	private async selectInChannel(channelId: string, personaId: string | null): Promise<void> {
		this.handleChannelStateUpdate({channel_id: channelId, persona_id: personaId});
		try {
			await http.put(ForkEndpoints.USER_PERSONA_CHANNEL_STATE(channelId), {body: {persona_id: personaId}});
		} catch {
			// Ask the server what holds now, instead of putting back a value that may itself be stale.
			await this.fetchChannelStates();
			Toast.error(i18n._(CHANNEL_PERSONA_UPDATE_FAILED_DESCRIPTOR));
		}
	}

	async recordPersonaUse(id: string): Promise<void> {
		const persona = this._personas.find((p) => p.id === id);
		if (persona) {
			runInAction(() => {
				persona.use_count = (persona.use_count ?? 0) + 1;
				persona.last_used_at_ms = Date.now().toString();
			});
		}
	}

	handleInChatCommand(channelId: string | null, content: string): {isCommand: boolean; handled: boolean} {
		if (content.trim() === '\\\\') {
			if (!this.latchedPersonaFor(channelId)) {
				return {isCommand: false, handled: false};
			}
			void this.unlatch(false, channelId);
			Toast.success(i18n._(ACTIVE_PERSONA_CLEARED_DESCRIPTOR));
			return {isCommand: true, handled: true};
		}
		return {isCommand: false, handled: false};
	}

	private matchablePersonas(): Array<PersonaLike> {
		return this._personas.map((p) => ({
			id: p.id,
			name: p.name,
			avatar_hash: p.avatar_hash ?? null,
			banner_hash: p.banner_hash ?? null,
			pronouns: p.pronouns ?? null,
			color: p.color ?? null,
			auto_tag_disabled: p.auto_tag_disabled ?? false,
			bio: p.bio ?? null,
			persona_tags: (p.persona_tags ?? []).map((t) => ({
				prefix: t.prefix ?? null,
				suffix: t.suffix ?? null,
			})),
		}));
	}

	matchOutgoingMessage(
		channelId: string | null,
		content: string,
		hasAttachments = false,
		options?: MatchPersonaOptions,
	): MatchResult {
		const personasLike = this.matchablePersonas();

		const activeLatchedId = this.latchedPersonaFor(channelId)?.id ?? null;
		const result = matchPersona(content, personasLike, activeLatchedId, hasAttachments, options);

		if (result.clearedLatch || (result.wasEscaped && this.activePersonaMode === 'last')) {
			void this.unlatch(false, channelId);
			Toast.success(i18n._(ACTIVE_PERSONA_CLEARED_ROOT_DESCRIPTOR));
		} else if (result.matched && result.persona) {
			void this.recordPersonaUse(result.persona.id);
			if (this.activePersonaMode === 'last' && activeLatchedId !== result.persona.id) {
				void this.setActivePersona(result.persona.id, true, 'last', channelId);
			}
		}

		return result;
	}

	/** The text matchOutgoingMessage would send, without recording a use or changing the active persona. */
	previewOutgoingContent(
		channelId: string | null,
		content: string,
		hasAttachments = false,
		options?: MatchPersonaOptions,
	): string {
		const result = matchPersona(
			content,
			this.matchablePersonas(),
			this.latchedPersonaFor(channelId)?.id ?? null,
			hasAttachments,
			options,
		);
		return result.matched || result.wasEscaped ? result.strippedContent : content;
	}

	/**
	 * Who `content` would be sent as. `isTagUnfinished` says the text may still become another
	 * persona's tag, in which case `persona` is only who it is for now and must not be shown to
	 * anyone else yet.
	 */
	getEffectivePersonaForText(
		channelId: string | null,
		content: string,
		hasAttachments = false,
	): {persona: ClientPersona | null; isFromTag: boolean; isTagUnfinished: boolean} {
		const personasLike = this.matchablePersonas();

		const activeLatchedId = this.latchedPersonaFor(channelId)?.id ?? null;
		const preview = previewPersona(content, personasLike, activeLatchedId, hasAttachments);
		const isTagUnfinished = isUnfinishedPersonaTag(content, personasLike, preview.persona?.id ?? null);

		if (preview.persona) {
			const found = this._personas.find((p) => p.id === preview.persona!.id) ?? null;
			return {persona: found, isFromTag: preview.isFromTag, isTagUnfinished};
		}

		return {persona: null, isFromTag: false, isTagUnfinished};
	}

	getPersonaBySignatureEmoji(emoji: {
		id?: string | null;
		name: string;
		animated?: boolean;
		surrogates?: string;
	}): ClientPersona | null {
		for (const persona of this._personas) {
			const sigs = persona.signature_emojis ?? [];
			for (const sig of sigs) {
				if (emoji.id && sig.id) {
					if (emoji.id === sig.id) return persona;
				} else if (!emoji.id && !sig.id) {
					if (emoji.name === sig.name) return persona;
					if (emoji.surrogates && emoji.surrogates === sig.name) return persona;
				}
			}
		}
		return null;
	}

	getEffectiveReactionPersona(
		channelId: string | null,
		emoji?: {id?: string | null; name: string; animated?: boolean; surrogates?: string} | null,
		composerText?: string | null,
	): ClientPersona | null {
		if (emoji) {
			const sigPersona = this.getPersonaBySignatureEmoji(emoji);
			if (sigPersona) {
				return sigPersona;
			}
		}
		if (composerText) {
			const fromText = this.getEffectivePersonaForText(channelId, composerText);
			if (fromText.isFromTag && fromText.persona) {
				return fromText.persona;
			}
		}
		return this.latchedPersonaFor(channelId);
	}

	matchEditMessage(
		content: string,
		currentSubprofile?: MessageSubprofileResponse | null,
		options?: {
			hasAttachments?: boolean;
			originalContent?: string;
		},
	): {
		finalContent: string;
		subprofile?: MessageSubprofileResponse | null;
	} {
		const personasLike = this.matchablePersonas();
		// A message whose text already starts with a backslash (sent as `a: \o/`) keeps it through every
		// edit: only a backslash the edit adds removes the persona (tyne-chat/tyne#158, audit M3-1).
		const originalStartsWithBackslash = options?.originalContent?.startsWith('\\') ?? false;

		// If user typed \ or \\ to explicitly clear active persona / escape
		if (content.startsWith('\\') && currentSubprofile && !originalStartsWithBackslash) {
			let strippedContent = '';
			if (content.startsWith('\\\\')) {
				const rawRest = content.slice(2);
				strippedContent = rawRest.startsWith(' ') ? rawRest.slice(1) : rawRest;
			} else {
				const rawRest = content.slice(1);
				strippedContent = rawRest.startsWith(' ') ? rawRest.slice(1) : rawRest;
			}
			return {
				finalContent: strippedContent,
				subprofile: null,
			};
		}

		// In edit mode, check explicit persona tags. Pass activeLatchedPersonaId as null so it never falls back to latched persona.
		const result = matchPersona(content, personasLike, null, true);

		// If explicit persona tags matched a persona, adopt that persona
		if (result.matched && result.persona) {
			return {
				finalContent: result.strippedContent,
				subprofile: normalizeSubprofile(result.persona as any, {
					display_tag_text: this.displayTagText || null,
					display_tag_icon: this.displayTagIcon || null,
				}),
			};
		}

		// No tag matched: the edit says nothing about the persona, and the message keeps the one it has.
		return {
			finalContent: content,
			subprofile: undefined,
		};
	}

	getActiveSubprofile(channelId: string | null): MessageSubprofileResponse | null {
		const active = this.latchedPersonaFor(channelId);
		if (!active) return null;
		return normalizeSubprofile(active, {
			display_tag_text: this.displayTagText || null,
			display_tag_icon: this.displayTagIcon || null,
		});
	}
}

export const PersonaStore = new PersonaStoreClass();

// Personas belong to one account: drop them whenever the app logs out or switches account.
AccountScopedWork.registerCancellation(() => PersonaStore.reset());
